import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WorkshopTypeManager.css';

const emptyType = {
  name: '',
  slug: '',
  description: '',
  duration_minutes: 90,
  price: 0,
  sku: '',
  category: 'candle_making',
  min_capacity: 1,
  max_capacity: 6,
  min_age: 0,
  requires_deposit: false,
  deposit_amount: 0,
  color: '#8B6F4E',
  is_active: true
};

function WorkshopTypeManager() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ ...emptyType });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/workshops/types');
      setTypes(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch workshop types:', err);
      setError('Failed to load workshop types');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (type) => {
    setEditingType(type.id);
    setFormData({ ...type });
  };

  const handleNew = () => {
    setEditingType('new');
    setFormData({ ...emptyType });
  };

  const handleCancel = () => {
    setEditingType(null);
    setFormData({ ...emptyType });
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...formData,
        price: Math.round(parseFloat(formData.price) * 100) || 0,
        deposit_amount: Math.round(parseFloat(formData.deposit_amount) * 100) || 0
      };

      if (editingType === 'new') {
        await axios.post('/api/workshops/types', payload);
      } else {
        await axios.put(`/api/workshops/types/${editingType}`, payload);
      }
      setEditingType(null);
      setFormData({ ...emptyType });
      await fetchTypes();
    } catch (err) {
      console.error('Failed to save workshop type:', err);
      alert('Failed to save workshop type');
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (cents) => {
    if (!cents && cents !== 0) return '--';
    return `$${(cents / 100).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="workshop-type-manager">
        <div className="ws-loading">
          <div className="spinner"></div>
          <p>Loading workshop types...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="workshop-type-manager">
      <div className="ws-page-header">
        <h1>Workshop Types</h1>
        <button className="ws-btn-primary" onClick={handleNew}>
          Add Type
        </button>
      </div>

      {error && <div className="ws-error">{error}</div>}

      {editingType && (
        <div className="ws-section ws-edit-form">
          <h2>{editingType === 'new' ? 'New Workshop Type' : 'Edit Workshop Type'}</h2>

          <div className="ws-form-grid">
            <div className="ws-form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g. Custom Candle Workshop"
              />
            </div>
            <div className="ws-form-group">
              <label>Slug</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="e.g. custom-candle"
              />
            </div>
            <div className="ws-form-group ws-form-full">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                placeholder="Describe the workshop experience..."
              />
            </div>
            <div className="ws-form-group">
              <label>Duration (minutes)</label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => handleChange('duration_minutes', parseInt(e.target.value) || 0)}
                min="15"
              />
            </div>
            <div className="ws-form-group">
              <label>Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={(formData.price / 100).toFixed(2)}
                onChange={(e) => handleChange('price', Math.round(parseFloat(e.target.value) * 100) || 0)}
                min="0"
              />
            </div>
            <div className="ws-form-group">
              <label>SKU</label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => handleChange('sku', e.target.value)}
              />
            </div>
            <div className="ws-form-group">
              <label>Category</label>
              <select value={formData.category} onChange={(e) => handleChange('category', e.target.value)}>
                <option value="candle_making">Candle Making</option>
                <option value="fragrance">Fragrance</option>
                <option value="private_party">Private Party</option>
                <option value="corporate">Corporate</option>
                <option value="special_event">Special Event</option>
              </select>
            </div>
            <div className="ws-form-group">
              <label>Min Capacity</label>
              <input
                type="number"
                value={formData.min_capacity}
                onChange={(e) => handleChange('min_capacity', parseInt(e.target.value) || 1)}
                min="1"
              />
            </div>
            <div className="ws-form-group">
              <label>Max Capacity</label>
              <input
                type="number"
                value={formData.max_capacity}
                onChange={(e) => handleChange('max_capacity', parseInt(e.target.value) || 1)}
                min="1"
              />
            </div>
            <div className="ws-form-group">
              <label>Minimum Age</label>
              <input
                type="number"
                value={formData.min_age}
                onChange={(e) => handleChange('min_age', parseInt(e.target.value) || 0)}
                min="0"
              />
            </div>
            <div className="ws-form-group">
              <label>Requires Deposit</label>
              <select
                value={formData.requires_deposit ? 'true' : 'false'}
                onChange={(e) => handleChange('requires_deposit', e.target.value === 'true')}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
            {formData.requires_deposit && (
              <div className="ws-form-group">
                <label>Deposit Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={(formData.deposit_amount / 100).toFixed(2)}
                  onChange={(e) => handleChange('deposit_amount', Math.round(parseFloat(e.target.value) * 100) || 0)}
                  min="0"
                />
              </div>
            )}
            <div className="ws-form-group">
              <label>Calendar Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="color"
                  value={formData.color || '#8B6F4E'}
                  onChange={(e) => handleChange('color', e.target.value)}
                  style={{ width: '48px', height: '36px', padding: '2px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                />
                <span style={{ color: '#666', fontSize: '13px' }}>{formData.color || '#8B6F4E'}</span>
              </div>
            </div>
            <div className="ws-form-group">
              <label>Active</label>
              <select
                value={formData.is_active ? 'true' : 'false'}
                onChange={(e) => handleChange('is_active', e.target.value === 'true')}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="ws-form-actions">
            <button className="ws-btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Type'}
            </button>
            <button className="ws-btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="ws-section">
        <h2>Workshop Types</h2>
        {types.length === 0 ? (
          <div className="ws-empty">No workshop types configured</div>
        ) : (
          <div className="ws-table-container">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>Color</th>
                  <th>Name</th>
                  <th>Duration</th>
                  <th>Price</th>
                  <th>Category</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((type) => (
                  <tr key={type.id}>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: type.color || '#8B6F4E'
                        }}
                      />
                    </td>
                    <td className="ws-text-bold">{type.name}</td>
                    <td>{type.duration_minutes} min</td>
                    <td>{formatPrice(type.price)}</td>
                    <td>
                      <span className="ws-source-tag">
                        {(type.category || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>{type.min_capacity}-{type.max_capacity}</td>
                    <td>
                      <span className={`ws-badge ${type.is_active ? 'ws-badge-confirmed' : 'ws-badge-cancelled'}`}>
                        {type.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button className="ws-btn-secondary ws-btn-sm" onClick={() => handleEdit(type)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorkshopTypeManager;
